import { useEffect, useState } from "react";
import { Megaphone, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

function daysLeft(endsAt) {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
}

const trackClick = (message) => {
  api.post("/analytics/banner-click", { message }).catch(() => {});
};

export default function AnnouncementBar() {
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    api
      .get("/settings/public")
      .then((r) => {
        const a = r.data?.announcement;
        if (a && a.message && sessionStorage.getItem("kinos_ann_dismissed") !== a.message) {
          setAnn(a);
        }
      })
      .catch(() => {});
  }, []);

  const visible = ann && !dismissed;

  useEffect(() => {
    document.documentElement.style.setProperty("--announce-h", visible ? "38px" : "0px");
    return () => document.documentElement.style.setProperty("--announce-h", "0px");
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="announcement-bar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#ED1C24] text-white"
    >
      <div className="px-6 h-[38px] flex items-center justify-center gap-4 text-xs sm:text-sm">
        <Megaphone size={14} className="shrink-0 hidden sm:block" />
        <span className="font-medium truncate">{ann.message}</span>
        {ann.code && (
          <span className="hidden sm:inline bg-white/20 px-2.5 py-0.5 rounded-full text-[11px] tracking-widest font-semibold">
            {ann.code}
          </span>
        )}
        {daysLeft(ann.ends_at) !== null && (
          <span
            data-testid="announcement-countdown"
            className={`hidden md:inline text-[11px] italic whitespace-nowrap ${
              daysLeft(ann.ends_at) <= 3
                ? "text-[#FFC61A] not-italic font-semibold"
                : "text-white/70"
            }`}
          >
            {daysLeft(ann.ends_at) === 0
              ? t("ann_ends_today")
              : t("ann_ends_in").replace("{n}", daysLeft(ann.ends_at))}
          </span>
        )}
        {ann.link && (
          <a
            data-testid="announcement-link"
            href={ann.link}
            onClick={() => trackClick(ann.message)}
            className="underline underline-offset-4 font-semibold whitespace-nowrap hover:text-[#FFC61A] transition-colors"
          >
            {ann.link_label || "Shop now"} →
          </a>
        )}
        <button
          data-testid="announcement-dismiss"
          onClick={() => {
            setDismissed(true);
            sessionStorage.setItem("kinos_ann_dismissed", ann.message);
          }}
          className="absolute right-4 text-white/70 hover:text-white transition-colors"
          aria-label="Dismiss announcement"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
